import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { ShowAllMarkdownButtons } from "abreact/input/markdown/ShowAllMarkdownButtons";
import { ArtificialNode__DO_NOT_USE } from "lexical";
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { TEST_DATA_LOADED } from "abreact/input/markdown/AbMarkdownLexicalContent";
import { AbMarkdownTextEditor } from "abreact/input/markdown/AbMarkdownTextEditor";
import { AbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
import { $createAbstractFieldNode, AbstractFieldNode } from "abreact/input/markdown/nodes/AbstractFieldNode";
import { PersonNode } from "abreact/input/markdown/nodes/PersonNode";
import AbstractFieldPlugin from "abreact/input/markdown/plugins/AbstractFieldPlugin";
import { OnChangeMarkdownPlugin } from "abreact/input/markdown/plugins/OnChangeMarkdownPlugin";
import PersonPlugin from "abreact/input/markdown/plugins/PersonPlugin";
import ExampleTheme from 'abreact/input/markdown/ui/ExampleTheme';
import { convertFromMarkdownString } from "abreact/input/markdown/utils/MarkdownImport";
import { AB_MARKDOWN_TRANSFORMERS } from 'abreact/input/markdown/utils/MarkdownTransformers';
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { $insertNodes, COMMAND_PRIORITY_LOW } from "lexical";
import * as React from "react";
import { ImageNode } from "./nodes/ImageNode";
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
// DEBUG ONLY: import TreeViewPlugin from "abreact/input/markdown/plugins/TreeViewPlugin";

// Prevent warnings for a Lexical@0.15.0 bug: ArtificialNode was added incorrectly, and safety checks in Lexical.dev.mjs
// lines 9144, 9163, and 9168 all went off.  Plug those holes with the simplest possible implementations.
ArtificialNode__DO_NOT_USE.clone = _data => {
  return new ArtificialNode__DO_NOT_USE();
};
ArtificialNode__DO_NOT_USE.importJSON = _serializedNode => {
  return new ArtificialNode__DO_NOT_USE();
};
ArtificialNode__DO_NOT_USE.prototype.exportJSON = () => {
  return {};
};
const editorConfig = {
  namespace: 'AbMarkdownLexical',
  nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode,
  // CodeHighlightNode,
  TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, ImageNode, HorizontalRuleNode, AbLinkNode, PersonNode, AbstractFieldNode],
  // Handling of errors during update
  onError(error) {
    console.error(error);
  },
  // The editor theme
  theme: ExampleTheme
};
const buttonsDisabledWithoutEasterEgg = {
  color: true,
  background: true,
  textMode: true
};
export function AbMarkdownLexical(props) {
  // Lexical limits languages to the the prism languages, make "dml" be a prism language.
  // TODO: This will be fixed when we upgrade Lexical.
  window.Prism.languages = {
    dml: {
      __id: -1
    },
    ...window.Prism.languages
  };

  // text mode
  let [showText, setShowText] = React.useState(false);
  // value of markdown
  let [markdown, setMarkdown] = React.useState(props.value);
  // html element for hanging toolbars
  const [floatingAnchorElem, setFloatingAnchorElem] = React.useState(null);
  // a ref to the editor for using outside <LexicalComposer>
  const tempRef = React.useRef(null);
  const editorRef = props.editorRef ?? tempRef;
  const disabledEditorTools = React.useMemo(() => {
    return props.disabledEditorTools && Object.keys(props.disabledEditorTools).length > 0 ? props.disabledEditorTools // passed in props win
    : ShowAllMarkdownButtons.easterEgg.isEnabled() ? {} // otherwise show all easterEgg is on
    : buttonsDisabledWithoutEasterEgg; // otherwise disable some
  }, [props.disabledEditorTools]);
  const {
    disabled,
    ...inputProps
  } = ReactUtils.inputProps("ab-markdown-editor", props, [props.resizable && "ab-resizable"]);

  // initial editor converts markdown from the props
  let config = {
    ...editorConfig,
    editable: !disabled,
    editorState: () => convertFromMarkdownString(markdown || "", AB_MARKDOWN_TRANSFORMERS, undefined, props.preserveNewLines)
  };
  React.useEffect(() => {
    props.forwardFocusRef?.whenExistsTakeFocusOnce();
  }, [props.forwardFocusRef]);
  React.useEffect(() => {
    if (props.value !== markdown) {
      editorRef.current?.update(() => {
        convertFromMarkdownString(props.value, AB_MARKDOWN_TRANSFORMERS, undefined, props.preserveNewLines);
      });
      setMarkdown(props.value);
    }
    // eslint-disable-next-line abinitio/exhaustive-deps
  }, [props.value]);
  React.useEffect(() => {
    editorRef.current?.setEditable(!props.disabled);
  }, [editorRef, props.disabled]);
  const onRef = _floatingAnchorElem => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };
  React.useEffect(() => {
    editorRef.current?.update(() => {
      if (props.nodeToInsert) {
        const nodeToInsert = $createAbstractFieldNode(props.nodeToInsert);
        $insertNodes([nodeToInsert]);
        if (props.resetInsert) {
          props.resetInsert();
        }
      }
    });
  }, [editorRef, props, props.nodeToInsert]);
  React.useEffect(() => {
    editorRef.current?.registerCommand(TEST_DATA_LOADED, () => {
      editorRef.current?.update(() => {
        //todo commented out for demo
        // convertFromMarkdownString(markdown, AB_MARKDOWN_TRANSFORMERS, undefined, props.preserveNewLines);
      });
      return false;
    }, COMMAND_PRIORITY_LOW);
  }, [editorRef]);
  const topLevelDivRef = React.useRef(null);
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: props.tooltip
  }, /*#__PURE__*/React.createElement("div", _extends({}, inputProps, {
    onClick: () => editorRef.current?.focus(),
    onBlur: () => props.onBlur?.(),
    onFocus: () => props.onFocus?.(),
    ref: topLevelDivRef
  }), showText && /*#__PURE__*/React.createElement(AbMarkdownTextEditor, {
    value: markdown,
    onChange: value => {
      setMarkdown(value); // change internal useState
      props.onChange(value); // tell our caller
    },
    disabled: disabled,
    disabledEditorTools: disabledEditorTools,
    setShowText: setShowText
  }), !showText && /*#__PURE__*/React.createElement(LexicalComposer, {
    initialConfig: config
  }, /*#__PURE__*/React.createElement(ToolbarPlugin, {
    floating: props.floatingToolbar,
    origin: topLevelDivRef,
    markdown: markdown,
    setMarkdown: value => {
      const newVal = typeof value === "string" ? value : value(markdown);
      editorRef.current?.update(() => {
        convertFromMarkdownString(newVal, AB_MARKDOWN_TRANSFORMERS, undefined, props.preserveNewLines);
      });
    },
    showText: showText,
    setShowText: setShowText,
    disabledEditorTools: disabledEditorTools
  }), /*#__PURE__*/React.createElement("div", {
    className: ReactUtils.classNamesString(["editor-inner", props.floatingToolbar ? "markdown-toolbar-floating" : null])
  }, /*#__PURE__*/React.createElement(RichTextPlugin, {
    contentEditable: /*#__PURE__*/React.createElement("div", {
      className: "content-editor",
      ref: onRef
    }, /*#__PURE__*/React.createElement(ContentEditable, null)),
    placeholder: props.placeholder ? /*#__PURE__*/React.createElement("div", {
      className: "placeholder"
    }, props.placeholder) : null,
    ErrorBoundary: LexicalErrorBoundary
  }), /*#__PURE__*/React.createElement(HistoryPlugin, null), props.autoFocus && /*#__PURE__*/React.createElement(AutoFocusPlugin, null), /*#__PURE__*/React.createElement(EditorRefPlugin, {
    editorRef: x => {
      props.forwardFocusRef?.assignRef(x._rootElement);
      editorRef.current = x;
    }
  }), /*#__PURE__*/React.createElement(ListPlugin, null), /*#__PURE__*/React.createElement(LinkPlugin, null), /*#__PURE__*/React.createElement(PersonPlugin, null), /*#__PURE__*/React.createElement(AbstractFieldPlugin, {
    viewMode: "default"
  }), /*#__PURE__*/React.createElement(OnChangeMarkdownPlugin, {
    onChange: value => {
      setMarkdown(value);
      return props.onChange(value);
    }
  }), floatingAnchorElem && /*#__PURE__*/React.createElement(FloatingLinkEditorPlugin, {
    anchorElem: floatingAnchorElem
  })))));
}
//# sourceMappingURL=AbMarkdownLexical.js.map
